import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { TestResultsRegisterResponseRow } from '@risksmart-app/trpc/src/types';
import type {
  GetTestResultsQuery,
  Test_Result_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetTestResultsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import useEntityWhereFilter from 'src/hooks/useEntityWhereFilter';
import { createQueryHook } from 'src/utils';

type InternalArgs = {
  entityIds?: string[];
};

const useGetTestResultsGraphqlVariables = () => {
  const where = useEntityWhereFilter<Test_Result_Bool_Exp>(
    Parent_Type_Enum.TestResult,
    {
      RatingType: { _in: ['assessment', 'rating'] },
    }
  );

  return { where };
};

const useGetTestResultsBase = createQueryHook<
  InternalArgs,
  TestResultsRegisterResponseRow[],
  GetTestResultsQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.testResult.testResults.queryOptions({
      entityIds: args.entityIds,
    }),
  mapTrpcDataToGraphQL: (data) => ({ test_result: data }),
  graphqlDocument: GetTestResultsDocument,
  graphqlVariables: useGetTestResultsGraphqlVariables,
});

export type UseGetTestResultsArgs = Record<string, never>;

/**
 * Fetches test results with automatic entity filtering for both GraphQL and
 * tRPC paths. Entity filtering uses useEntityFilter context internally —
 * consumers don't need to pass entityIds.
 */
export const useGetTestResults = (params: {
  queryArgs: UseGetTestResultsArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetTestResultsBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
